// @ts-nocheck
import {
  Stack,
  Title,
  Text as MantineText,
  Code,
  Group,
  Divider,
  Avatar as MantineAvatar,
  Table as MantineTable,
} from '@mantine/core';
import { IconUser, IconStar, IconTrendingUp } from '@tabler/icons-react';
import { Card, Avatar, Stat, Timeline, AvatarGroup } from '../../../components/ui';
import { mockUsers, mockTableData, mockTimelineEvents } from '../mockData';

export default function DataDisplayPage() {
  return (
    <Stack gap="xl">
      {/* Page Header */}
      <Stack gap="xs">
        <Title order={2}>Data Display Components</Title>
        <MantineText c="dimmed" size="sm">
          Components for displaying data, including cards, avatars, tables, stats, and timelines
        </MantineText>
      </Stack>

      {/* Card Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Card</Title>
            <MantineText size="sm" c="dimmed">
              Flexible container component with multiple variants and hover effects
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Basic Card:</MantineText>
            <Card withBorder p="md" style={{ maxWidth: 400 }}>
              <Stack gap="xs">
                <Title order={5}>Default Card</Title>
                <MantineText size="sm" c="dimmed">
                  This is a basic card with border, padding, and content
                </MantineText>
              </Stack>
            </Card>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Card withBorder p="md">
  <Stack gap="xs">
    <Title order={5}>Default Card</Title>
    <Text size="sm" c="dimmed">
      This is a basic card with border, padding, and content
    </Text>
  </Stack>
</Card>`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Gradient Card with Hover Effect:</MantineText>
            <Card variant="gradient" gradientVariant="primary" p="md" hoverEffect="glow" style={{ maxWidth: 400 }}>
              <Stack gap="xs">
                <Title order={5} c="white">
                  Gradient Card
                </Title>
                <MantineText size="sm" c="white" opacity={0.9}>
                  Gradient background with glow hover effect
                </MantineText>
              </Stack>
            </Card>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Card variant="gradient" gradientVariant="primary" p="md" hoverEffect="glow">
  <Stack gap="xs">
    <Title order={5} c="white">Gradient Card</Title>
    <Text size="sm" c="white" opacity={0.9}>
      Gradient background with glow hover effect
    </Text>
  </Stack>
</Card>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • variant - 'default' | 'gradient'
              <br />
              • gradientVariant - 'primary' | 'secondary' | 'accent' (when variant="gradient")
              <br />
              • hoverEffect - 'lift' | 'glow' | 'scale'
              <br />
              • withBorder - boolean to add border
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* Avatar Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Avatar</Title>
            <MantineText size="sm" c="dimmed">
              Display user profile pictures or initials with various sizes and styles
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Basic Avatars:</MantineText>
            <Group>
              <Avatar src={mockUsers[0].avatar} alt={mockUsers[0].name} />
              <Avatar src={mockUsers[1].avatar} alt={mockUsers[1].name} size="lg" />
              <Avatar name={mockUsers[2].name} color="blue" />
              <Avatar name={mockUsers[3].name} color="green" variant="filled" />
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Avatar src="/avatars/user.jpg" alt="John Doe" />
<Avatar src="/avatars/user.jpg" alt="John Doe" size="lg" />
<Avatar name="John Doe" color="blue" />
<Avatar name="Jane Smith" color="green" variant="filled" />`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Sizes:</MantineText>
            <Group align="center">
              <Avatar src={mockUsers[0].avatar} size="xs" />
              <Avatar src={mockUsers[0].avatar} size="sm" />
              <Avatar src={mockUsers[0].avatar} size="md" />
              <Avatar src={mockUsers[0].avatar} size="lg" />
              <Avatar src={mockUsers[0].avatar} size="xl" />
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Avatar src="/avatar.jpg" size="xs" />
<Avatar src="/avatar.jpg" size="sm" />
<Avatar src="/avatar.jpg" size="md" />
<Avatar src="/avatar.jpg" size="lg" />
<Avatar src="/avatar.jpg" size="xl" />`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Avatar Group:</MantineText>
            <AvatarGroup
              avatars={mockUsers.slice(0, 6).map((user) => ({
                src: user.avatar,
                name: user.name,
              }))}
              max={4}
            />
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<AvatarGroup
  avatars={users.map((user) => ({
    src: user.avatar,
    name: user.name,
  }))}
  max={4}
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Stat Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Stat</Title>
            <MantineText size="sm" c="dimmed">
              Display statistics and metrics with labels, values, and optional trends
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Basic Stat:</MantineText>
            <Group grow>
              <Stat label="Total Users" value="1,234" />
              <Stat label="Revenue" value="$45,678" />
              <Stat label="Active Projects" value="23" />
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Stat label="Total Users" value="1,234" />
<Stat label="Revenue" value="$45,678" />
<Stat label="Active Projects" value="23" />`}
            </Code>
          </Stack>

          <Divider />

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>With Icons and Trends:</MantineText>
            <Group grow>
              <Stat
                label="Total Users"
                value="1,234"
                icon={<IconUser size={20} />}
                trend={{ value: 12, type: 'increase' }}
                trendLabel="vs last month"
              />
              <Stat
                label="Rating"
                value="4.8"
                icon={<IconStar size={20} />}
                trend={{ value: 0.3, type: 'increase' }}
                trendLabel="vs last week"
              />
              <Stat
                label="Growth"
                value="23%"
                icon={<IconTrendingUp size={20} />}
                trend={{ value: 5, type: 'increase' }}
                trendLabel="this quarter"
              />
            </Group>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Stat
  label="Total Users"
  value="1,234"
  icon={<IconUser size={20} />}
  trend={{ value: 12, type: 'increase' }}
  trendLabel="vs last month"
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Table Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Table</Title>
            <MantineText size="sm" c="dimmed">
              Display tabular data with Mantine's Table component
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Basic Table:</MantineText>
            <MantineTable striped highlightOnHover withTableBorder>
              <MantineTable.Thead>
                <MantineTable.Tr>
                  <MantineTable.Th>Name</MantineTable.Th>
                  <MantineTable.Th>Email</MantineTable.Th>
                  <MantineTable.Th>Role</MantineTable.Th>
                  <MantineTable.Th>Department</MantineTable.Th>
                </MantineTable.Tr>
              </MantineTable.Thead>
              <MantineTable.Tbody>
                {mockTableData.slice(0, 5).map((row) => (
                  <MantineTable.Tr key={row.id}>
                    <MantineTable.Td>{row.name}</MantineTable.Td>
                    <MantineTable.Td>{row.email}</MantineTable.Td>
                    <MantineTable.Td>{row.role}</MantineTable.Td>
                    <MantineTable.Td>{row.department}</MantineTable.Td>
                  </MantineTable.Tr>
                ))}
              </MantineTable.Tbody>
            </MantineTable>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Table striped highlightOnHover withTableBorder>
  <Table.Thead>
    <Table.Tr>
      <Table.Th>Name</Table.Th>
      <Table.Th>Email</Table.Th>
      <Table.Th>Role</Table.Th>
    </Table.Tr>
  </Table.Thead>
  <Table.Tbody>
    {data.map((row) => (
      <Table.Tr key={row.id}>
        <Table.Td>{row.name}</Table.Td>
        <Table.Td>{row.email}</Table.Td>
        <Table.Td>{row.role}</Table.Td>
      </Table.Tr>
    ))}
  </Table.Tbody>
</Table>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Timeline Component */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Timeline</Title>
            <MantineText size="sm" c="dimmed">
              Display chronological events with titles, descriptions, and timestamps
            </MantineText>
          </Stack>

          <Stack gap="sm">
            <MantineText size="sm" fw={500}>Example Timeline:</MantineText>
            <Timeline
              items={mockTimelineEvents.slice(0, 4).map((event) => ({
                title: event.title,
                description: event.description,
                timestamp: event.timestamp,
              }))}
            />
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Timeline
  items={[
    {
      title: 'Project created',
      description: 'Website Redesign project was created',
      timestamp: new Date('2024-01-10T09:00:00'),
    },
    {
      title: 'Task assigned',
      description: 'New task assigned to John Doe',
      timestamp: new Date('2024-01-11T10:30:00'),
    },
    // ... more items
  ]}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Key Props:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • items - Array of timeline items with title, description, timestamp
              <br />
              • Each item can include optional icon and color
            </MantineText>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
